import type { PipelineType } from 'src/utils/enumerations';

import React from 'react';

import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from 'src/utils/constants';

import { TableHeadCustom, TablePaginationCustom } from 'src/components/table';
import { ComplianceTableSkeleton } from 'src/components/table/table-skeleton';

import CustomEmptyTableState from 'src/sections/sales/home/CustomEmptyTableState';

import UploadTableRow from './UploadTableRow';

import type { TableColumn } from './types';

interface UploadTableProps {
  /**
   * Columns configuration for the table.
   */
  columns: TableColumn[];
  /**
   * Data rows to be displayed in the table.
   */
  rows: any[];
  /**
   * Flag indicating whether the table is in a loading state.
   */
  isLoading: boolean;
  /**
   * Total count of rows for pagination.
   */
  totalCount: number;
  /**
   * Pagination options including page number, page size, sort by, and sort order.
   */
  paginationOptions: {
    page: number;
    page_size: number;
    sort_by: string;
    sort_order: 'desc' | 'asc';
    analysis_type: PipelineType;
  };
  /**
   * Callback function to handle sorting.
   */
  onSort: (orderBy: string) => void;
  /**
   * Callback function to handle page change.
   */
  onPageChange: (event: unknown, newPage: number) => void;
  /**
   * Callback function to handle rows per page change.
   */
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Callback function to handle dense mode change.
   */
  onChangeDense: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Callback function to handle retry run config.
   */
  onTryAgain: (intentId: string, runConfigId: string) => void;
  /**
   * Callback function to handle summary click.
   */
  onSummaryClick: (runConfigId: string) => void;
  /**
   * Flag indicating whether the table is in dense mode.
   */
  dense: boolean;
  /**
   * Custom skeleton component to be displayed while loading.
   */
  skeleton?: React.FC<any>;
}

const UploadTable: React.FC<UploadTableProps> = ({
  columns,
  rows,
  isLoading,
  totalCount,
  paginationOptions,
  onSort,
  onPageChange,
  onRowsPerPageChange,
  onChangeDense,
  onTryAgain,
  onSummaryClick,
  dense,
  skeleton = ComplianceTableSkeleton,
}) => (
  <Paper
    sx={(theme) => ({
      boxShadow: theme.customShadows.card,
    })}
  >
    <TableContainer>
      <Table size={dense ? 'small' : 'medium'}>
        <TableHeadCustom
          headLabel={columns}
          orderBy={paginationOptions.sort_by}
          order={paginationOptions.sort_order}
          onSort={onSort}
        />
        <TableBody>
          {isLoading && React.createElement(skeleton)}
          {!isLoading &&
            rows.length > 0 &&
            rows.map((row) => (
              <UploadTableRow
                key={row.id}
                row={{ ...row, onTryAgain, onSummaryClick }}
                columns={columns}
              />
            ))}
          {!isLoading && rows.length === 0 && <CustomEmptyTableState />}
        </TableBody>
      </Table>
      <TablePaginationCustom
        count={totalCount}
        rowsPerPage={paginationOptions.page_size}
        page={paginationOptions.page - 1} // Adjusting for 0-based index
        onPageChange={onPageChange}
        rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
        onRowsPerPageChange={onRowsPerPageChange}
        onChangeDense={onChangeDense}
        dense={dense}
      />
    </TableContainer>
  </Paper>
);

export default UploadTable;
